// src/services/supabaseService.js
import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toCamelCase);
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(toSnakeCase);
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
      result[snakeKey] = toSnakeCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

// ============================================
// MERCHANTS SERVICE
// ============================================

export const merchantsService = {
  // Get all merchants
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('merchants')?.select('*')?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching merchants:', error);
      return { data: null, error };
    }
  },

  // Get merchant by ID
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('merchants')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching merchant:', error);
      return { data: null, error };
    }
  },

  // Get merchant statistics
  async getStatistics() {
    try {
      const { data, error } = await supabase?.from('merchants')?.select('status, total_transactions, total_revenue, success_rate, churn_risk_score');

      if (error) throw error;

      const stats = {
        totalMerchants: data?.length || 0,
        activeMerchants: data?.filter(m => m?.status === 'active')?.length || 0,
        avgSuccessRate: data?.reduce((sum, m) => sum + (m?.success_rate || 0), 0) / (data?.length || 1),
        avgChurnRisk: data?.reduce((sum, m) => sum + (m?.churn_risk_score || 0), 0) / (data?.length || 1)
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching merchant statistics:', error);
      return { data: null, error };
    }
  },

  // Create merchant
  async create(merchantData) {
    try {
      const { data, error } = await supabase?.from('merchants')?.insert(toSnakeCase(merchantData))?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating merchant:', error);
      return { data: null, error };
    }
  },

  // Update merchant
  async update(id, merchantData) {
    try {
      const { data, error } = await supabase?.from('merchants')?.update(toSnakeCase(merchantData))?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating merchant:', error);
      return { data: null, error };
    }
  },

  // Subscribe to merchant changes
  subscribe(callback) {
    const channel = supabase?.channel('merchants_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'merchants'
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

// ============================================
// TRANSACTIONS SERVICE
// ============================================

export const transactionsService = {
  // Get all transactions
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('transactions')?.select('*, merchants(merchant_name, merchant_id)')?.order('timestamp', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.merchantId) {
        query = query?.eq('merchant_id', filters?.merchantId);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return { data: null, error };
    }
  },

  // Get transaction by ID
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('transactions')?.select('*, merchants(merchant_name, merchant_id)')?.eq('id', id)?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching transaction:', error);
      return { data: null, error };
    }
  },

  // Get transaction statistics
  async getStatistics(filters = {}) {
    try {
      let query = supabase?.from('transactions')?.select('status, amount, processing_time');

      if (filters?.startDate) {
        query = query?.gte('timestamp', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('timestamp', filters?.endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      const totalVolume = data?.length || 0;
      const successCount = data?.filter(t => t?.status === 'success')?.length || 0;
      const totalRevenue = data?.reduce((sum, t) => sum + (parseFloat(t?.amount) || 0), 0);
      const avgProcessingTime = data?.filter(t => t?.processing_time)?.reduce((sum, t) => sum + t?.processing_time, 0) / (data?.filter(t => t?.processing_time)?.length || 1);

      const stats = {
        totalVolume,
        successRate: totalVolume > 0 ? (successCount / totalVolume) * 100 : 0,
        totalRevenue,
        avgProcessingTime: Math.round(avgProcessingTime || 0)
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching transaction statistics:', error);
      return { data: null, error };
    }
  },

  // Create transaction
  async create(transactionData) {
    try {
      const { data, error } = await supabase?.from('transactions')?.insert(toSnakeCase(transactionData))?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating transaction:', error);
      return { data: null, error };
    }
  },

  // Update transaction
  async update(id, transactionData) {
    try {
      const { data, error } = await supabase?.from('transactions')?.update(toSnakeCase(transactionData))?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating transaction:', error);
      return { data: null, error };
    }
  },

  // Subscribe to transaction changes
  subscribe(callback, filters = {}) {
    let channelConfig = {
      event: '*',
      schema: 'public',
      table: 'transactions'
    };

    if (filters?.merchantId) {
      channelConfig.filter = `merchant_id=eq.${filters?.merchantId}`;
    }

    const channel = supabase?.channel('transactions_changes')?.on('postgres_changes', channelConfig, (payload) => {
        callback(toCamelCase(payload));
      })?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

// ============================================
// FRAUD ALERTS SERVICE
// ============================================

export const fraudAlertsService = {
  // Get all fraud alerts
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('fraud_alerts')?.select('*, merchants(merchant_name, merchant_id)')?.order('timestamp', { ascending: false });

      if (filters?.severity) {
        query = query?.eq('severity', filters?.severity);
      }

      if (filters?.isResolved !== undefined) {
        query = query?.eq('is_resolved', filters?.isResolved);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching fraud alerts:', error);
      return { data: null, error };
    }
  },

  // Get fraud alert by ID
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('fraud_alerts')?.select('*, merchants(merchant_name, merchant_id)')?.eq('id', id)?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching fraud alert:', error);
      return { data: null, error };
    }
  },

  // Get fraud statistics
  async getStatistics() {
    try {
      const { data, error } = await supabase?.from('fraud_alerts')?.select('severity, risk_score, is_resolved');

      if (error) throw error;

      const stats = {
        totalAlerts: data?.length || 0,
        criticalAlerts: data?.filter(a => a?.severity === 'critical')?.length || 0,
        highAlerts: data?.filter(a => a?.severity === 'high')?.length || 0,
        unresolvedAlerts: data?.filter(a => !a?.is_resolved)?.length || 0,
        avgRiskScore: data?.reduce((sum, a) => sum + (a?.risk_score || 0), 0) / (data?.length || 1)
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching fraud statistics:', error);
      return { data: null, error };
    }
  },

  // Create fraud alert
  async create(alertData) {
    try {
      const { data, error } = await supabase?.from('fraud_alerts')?.insert(toSnakeCase(alertData))?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating fraud alert:', error);
      return { data: null, error };
    }
  },

  // Update fraud alert (resolve)
  async resolve(id, resolvedBy) {
    try {
      const { data, error } = await supabase?.from('fraud_alerts')?.update({
          is_resolved: true,
          resolved_at: new Date()?.toISOString(),
          resolved_by: resolvedBy
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error resolving fraud alert:', error);
      return { data: null, error };
    }
  },

  // Subscribe to fraud alert changes
  subscribe(callback, filters = {}) {
    let channelConfig = {
      event: '*',
      schema: 'public',
      table: 'fraud_alerts'
    };

    if (filters?.severity) {
      channelConfig.filter = `severity=eq.${filters?.severity}`;
    }

    const channel = supabase?.channel('fraud_alerts_changes')?.on('postgres_changes', channelConfig, (payload) => {
        callback(toCamelCase(payload));
      })?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

// ============================================
// SETTLEMENTS SERVICE
// ============================================

export const settlementsService = {
  // Get all settlements
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('settlements')?.select('*, merchants(merchant_name, merchant_id)')?.order('settlement_date', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.hasDiscrepancy !== undefined) {
        query = query?.eq('has_discrepancy', filters?.hasDiscrepancy);
      }

      if (filters?.merchantId) {
        query = query?.eq('merchant_id', filters?.merchantId);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching settlements:', error);
      return { data: null, error };
    }
  },

  // Get settlement by ID
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('settlements')?.select('*, merchants(merchant_name, merchant_id)')?.eq('id', id)?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching settlement:', error);
      return { data: null, error };
    }
  },

  // Get settlement statistics
  async getStatistics() {
    try {
      const { data, error } = await supabase?.from('settlements')?.select('status, gross_amount, net_amount, has_discrepancy, discrepancy_amount');

      if (error) throw error;

      const totalGross = data?.reduce((sum, s) => sum + (parseFloat(s?.gross_amount) || 0), 0);
      const totalNet = data?.reduce((sum, s) => sum + (parseFloat(s?.net_amount) || 0), 0);
      const totalDiscrepancy = data?.filter(s => s?.has_discrepancy)?.reduce((sum, s) => sum + (parseFloat(s?.discrepancy_amount) || 0), 0);

      const stats = {
        totalSettlements: data?.length || 0,
        completedSettlements: data?.filter(s => s?.status === 'completed')?.length || 0,
        pendingSettlements: data?.filter(s => s?.status === 'pending')?.length || 0,
        totalGrossAmount: totalGross,
        totalNetAmount: totalNet,
        totalDiscrepancy,
        discrepancyCount: data?.filter(s => s?.has_discrepancy)?.length || 0
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching settlement statistics:', error);
      return { data: null, error };
    }
  },

  // Create settlement
  async create(settlementData) {
    try {
      const { data, error } = await supabase?.from('settlements')?.insert(toSnakeCase(settlementData))?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating settlement:', error);
      return { data: null, error };
    }
  },

  // Update settlement
  async update(id, settlementData) {
    try {
      const { data, error } = await supabase?.from('settlements')?.update(toSnakeCase(settlementData))?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating settlement:', error);
      return { data: null, error };
    }
  },

  // Subscribe to settlement changes
  subscribe(callback) {
    const channel = supabase?.channel('settlements_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'settlements'
        },
        (payload) => {
          callback(toCamelCase(payload));
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

// NOTIFICATION SERVICE
// ============================================

export const notificationsService = {
  // Get all notifications for current user
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('notifications')?.select('*')?.order('created_at', { ascending: false });

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.type) {
        query = query?.eq('type', filters?.type);
      }

      if (filters?.severity) {
        query = query?.eq('severity', filters?.severity);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return { data: null, error };
    }
  },

  // Get unread count
  async getUnreadCount() {
    try {
      const { count, error } = await supabase?.from('notifications')?.select('*', { count: 'exact', head: true })?.eq('status', 'unread');

      if (error) throw error;
      return { data: count || 0, error: null };
    } catch (error) {
      console.error('Error fetching unread count:', error);
      return { data: 0, error };
    }
  },

  // Mark notification as read
  async markAsRead(id) {
    try {
      const { data, error } = await supabase?.from('notifications')?.update({
          status: 'read',
          read_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return { data: null, error };
    }
  },

  // Mark all as read
  async markAllAsRead() {
    try {
      const { data, error } = await supabase?.from('notifications')?.update({
          status: 'read',
          read_at: new Date()?.toISOString()
        })?.eq('status', 'unread')?.select();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error marking all as read:', error);
      return { data: null, error };
    }
  },

  // Archive notification
  async archive(id) {
    try {
      const { data, error } = await supabase?.from('notifications')?.update({ status: 'archived' })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error archiving notification:', error);
      return { data: null, error };
    }
  },

  // Create notification
  async create(notificationData) {
    try {
      const { data, error } = await supabase?.from('notifications')?.insert(toSnakeCase(notificationData))?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating notification:', error);
      return { data: null, error };
    }
  },

  // Subscribe to notification changes
  subscribe(callback, filters = {}) {
    let channelConfig = {
      event: '*',
      schema: 'public',
      table: 'notifications'
    };

    if (filters?.userId) {
      channelConfig.filter = `user_id=eq.${filters?.userId}`;
    }

    const channel = supabase?.channel('notifications_changes')?.on('postgres_changes', channelConfig, (payload) => {
        callback(toCamelCase(payload));
      })?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};

// NOTIFICATION PREFERENCES SERVICE
// ============================================

export const notificationPreferencesService = {
  // Get user preferences
  async get(userId) {
    try {
      const { data, error } = await supabase?.from('notification_preferences')?.select('*')?.eq('user_id', userId)?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      return { data: null, error };
    }
  },

  // Update preferences
  async update(userId, preferences) {
    try {
      const { data, error } = await supabase?.from('notification_preferences')?.update(toSnakeCase(preferences))?.eq('user_id', userId)?.select()?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      return { data: null, error };
    }
  }
};

// ============================================
// PAYMENT GATEWAYS SERVICE
// ============================================

export const paymentGatewaysService = {
  // Get all payment gateways
  async getAll() {
    try {
      const { data, error } = await supabase
        ?.from('payment_gateways')
        ?.select('*')
        ?.order('name', { ascending: true });

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching payment gateways:', error);
      return { data: null, error };
    }
  },

  // Get gateway by ID
  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('payment_gateways')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching payment gateway:', error);
      return { data: null, error };
    }
  },

  // Update gateway status
  async updateStatus(id, status) {
    try {
      const { data, error } = await supabase
        ?.from('payment_gateways')
        ?.update({ status })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating gateway status:', error);
      return { data: null, error };
    }
  },

  // Subscribe to real-time changes
  subscribe(callback) {
    const subscription = supabase
      ?.channel('payment_gateways_changes')
      ?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'payment_gateways' },
        (payload) => {
          const transformedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? toCamelCase(payload?.new) : null,
            old: payload?.old ? toCamelCase(payload?.old) : null
          };
          callback(transformedPayload);
        }
      )
      ?.subscribe();

    return () => {
      subscription?.unsubscribe();
    };
  }
};

export default {
  merchants: merchantsService,
  transactions: transactionsService,
  fraudAlerts: fraudAlertsService,
  settlements: settlementsService
};